<template>
  <div class="flex flex-col justify-between flex-1">
    <div class="divide-y divide-gray-200">
      <div class="pt-6 pb-5 space-y-6">
        <div v-if="!graph_object_type">
          <SelectInput
            v-if="!initialFilter"
            :items="$store.state.object_types"
            :value="graph_object_type"
            name="prospecting_filter"
            label-attr="label"
            value-attr="type"
            :allow-null="false"
            :inline-label="true"
            :show-label="false"
            :classes="`w-[21vw] mb-6`"
            :is-box-background="true"
            :label="$t('Graph for ?')"
            :return-ids="true"
            :placeholder="$t('Select object to create graph for')"
            @update:currentItem="graph_object_type = $event" />
        </div>
        <Form v-else
              v-slot="{ values }"
              :initial-values="initial_values"
              :validation-schema="schema"
              @submit="createGraph">
          <div class="gap-2 space-y-4">
            <div class="flex items-center justify-between gap-1 rounded-md shadow-sm">
              <TextInput
                :disabled="is_loading"
                :label="$t('Graph Name')"
                classes="w-3/6"
                :is-box-background="true"
                input-classes="appearance-none block w-full px-2 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                name="name"
                type="text"
                :show-label="true"
                :placeholder="$t('Ex: Closed deals')"
                @update:modelValue="formDataChanged(values)" />

              <SelectInput
                :disabled="is_loading"
                name="graph_type"
                classes="w-4/6"
                :show-label="true"
                :allow-null="false"
                :items="graph_types"
                label-attr="name"
                value-attr="value"
                :is-box-background="true"
                :placeholder="$t('Chart of your graph')"
                :label="$t('Chart type')"
                value=""
                input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                @update:currentItem="formDataChanged(values)" />
              <SelectInput
                :items="public_private"
                label-attr="name"
                value-attr="value"
                :allow-null="false"
                :value="public_private[0].value"
                name="is_public"
                :placeholder="$t('Public or private graph')"
                :is-box-background="true"
                :show-label="true"
                :label="$t('Type')"
                classes="w-2/6"
                input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                @update:currentItem="formDataChanged(values)" />
            </div>

            <div v-if="selected_series.length" class="flex justify-between">
              <label class="block text-base font-medium text-gray-700 dark:text-dark-body-text"> {{ $t("Series") }} </label>
              <div class="flex flex-row items-center gap-2">
                <label class="block text-base font-medium text-gray-700 dark:text-dark-body-text">{{ $t("Aggregate") }} </label>

                <Switch
                  v-model="aggregate_series"
                  :disabled="is_loading"
                  :class="[
                    aggregate_series ? 'bg-blue' : 'bg-gray-200 dark:bg-dark-screen-background',
                    'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                  ]">
                  <span
                    aria-hidden="true"
                    :class="[
                      aggregate_series && $store.state.portfolio.ui_options?.language == 'ar'
                        ? '-translate-x-5'
                        : aggregate_series
                          ? 'translate-x-5'
                          : 'translate-x-0',
                      'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                    ]" />
                </Switch>
              </div>
            </div>
            <div v-for="(serie, series_index) in selected_series" :key="serie" class="flex w-full gap-2">
              <ComboBoxInput
                v-model="serie.metric"
                :value="serie.metric"
                :disabled="is_loading"
                :items="series_metrics"
                :show-label="true"
                :allow-null="false"
                :return-null="false"
                :floating-label="true"
                label-attr="name"
                :name="`serie-${series_index}`"
                value-attr="id"
                :is-box-background="true"
                :label="$t('Serie')"
                classes="w-full"
                input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
                @update:currentItem="formDataChanged(values)" />

              <SelectInput
                v-if="values.graph_type == 'mixed_charts'"
                v-model="serie.chart_type"
                :value="chart_types[0].value"
                :disabled="is_loading"
                :show-label="true"
                :allow-null="false"
                :items="chart_types"
                :name="`charttype-${series_index}`"
                value-attr="value"
                label-attr="name"
                :placeholder="$t('Chart type')"
                label=""
                classes="w-1/2"
                input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light " />
              <SelectInput
                v-if="aggregate_series"
                v-model="serie.aggregation_method"
                :disabled="is_loading"
                :show-label="true"
                :allow-null="false"
                :items="metrics_aggregation_methods"
                :name="`agmeth-${series_index}`"
                value-attr="value"
                label-attr="name"
                :placeholder="$t('Aggregation method')"
                label=""
                classes="w-1/2"
                input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light "
                @update:currentItem="handleSelectAggregation($event, series_index)" />

              <div
                v-show="
                  !(
                    values.graph_type == 'pie' ||
                    values.graph_type == 'polarArea' ||
                    values.graph_type == 'radialBar' ||
                    values.graph_type == 'treemap'
                  )
                "
                class="h-10 w-10 !p-0 flex items-center has-tooltip">
                <!-- <input v-model="serie.space_color" label="" class="h-10 w-10 overflow-hidden appearance-none bg-transparent rounded-full !p-0" name="color" type="color" placeholder="Bar color" @input="formDataChanged(values)" /> -->

                <ColorPickerList v-model:current-item="serie.space_color" @update:currentItem="formDataChanged(values)" />

                <span class="mb-10 ml-5 text-sm font-medium text-gray-700 tooltip whitespace-nowrap group-hover:bg-black">
                  <RotateCw
                    class="self-center w-5 h-5 text-gray-600"
                    aria-hidden="true"
                    @click="
                      () => {
                        serie.space_color = $store.state.data.graphs_default_colors[series_index]
                      }
                    " />
                </span>
              </div>
              <button
                v-if="selected_series.length"
                type="button"
                class="m-0 border-transparent cancel-btn"
                @click="
                  () => {
                    selected_series.splice(
                      selected_series.findIndex((s) => s.id == serie.id),
                      1
                    )
                  }
                ">
                <Trash2 class="self-center w-5 h-5 text-red-400" aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              class="!mb-3 cancel-btn float-right"
              @click="
                () => {
                  selected_series.push({
                    id: selected_series.length + 1,
                    metric: series_metrics[0].id,
                    aggregation_method: 'average',
                    chart_type: chart_types[0].value,
                    space_color: $store.state.data.graphs_default_colors[selected_series.length]
                  })
                }
              ">
              {{ $t("Add Serie") }}
            </button>

            <div class="flex flex-col w-full gap-2">
              <div class="flex w-full gap-1 mb-2">
                <ComboBoxInput
                  classes="w-full"
                  :disabled="is_loading"
                  :items="axis_metrics"
                  :allow-null="true"
                  :is-box-background="true"
                  :show-label="true"
                  :floating-label="true"
                  name="axis"
                  label-attr="name"
                  value-attr="id"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                  :label="$t('Axis')"
                  @update:currentItem="formDataChanged(values)" />
                <ComboBoxInput
                  v-if="isDateBasedAxis(values)"
                  classes="w-2/6"
                  :disabled="is_loading"
                  :items="truncate_by_types"
                  :allow-null="false"
                  :is-box-background="true"
                  :show-label="true"
                  :floating-label="true"
                  name="truncate_by"
                  label-attr="name"
                  value-attr="value"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                  :label="$t('Truncate by')"
                  @update:currentItem="formDataChanged(values)" />
                <SelectInput
                  :disabled="is_loading"
                  name="order_sort"
                  classes="w-3/6"
                  :show-label="true"
                  :allow-null="false"
                  :items="order_sorts"
                  :is-box-background="true"
                  label-attr="name"
                  value-attr="value"
                  :label="$t('Order sort')"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                  @update:currentItem="formDataChanged(values)" />
                <ComboBoxInput
                  classes="w-2/6"
                  :disabled="is_loading"
                  :items="max_limits"
                  :show-label="true"
                  :allow-null="true"
                  :return-value="true"
                  :is-box-background="true"
                  :allow-typed-value="true"
                  :floating-label="true"
                  name="max_limit"
                  label-attr="name"
                  value-attr="value"
                  input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                  :label="$t('Top') + ' #'"
                  @update:currentItem="formDataChanged(values)" />
              </div>

              <div v-if="isDateBasedAxis(values)" class="flex w-full gap-1 mb-2">
                <DatePicker
                  ref="date_picker"
                  v-model="from_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="w-4/12 bg-white dark:bg-dark-screen-background"
                  is-inline
                  :input-debounce="500"
                  color="blue"
                  mode="date"
                  :popover="{ visibility: 'click' }"
                  @dayclick="formDataChanged(values)">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative flex-grow">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        :value="inputValue"
                        placeholder="Start date"
                        class="flex-grow w-full px-10 py-2 pl-8 pr-2 transition ease-in-out bg-white border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !isBoxBackground
                            ? 'bg-light-screen-background dark:bg-dark-screen-background'
                            : 'bg-light-box-background dark:bg-dark-box-background'
                        ]"
                        v-on="inputEvents">
                    </div>
                  </template>
                </DatePicker>
                <DatePicker
                  ref="date_picker"
                  v-model="to_date"
                  :is-dark="$store.state.ui.darkMode"
                  class="w-4/12 bg-white dark:bg-dark-screen-background"
                  is-inline
                  :input-debounce="500"
                  color="blue"
                  mode="date"
                  :popover="{ visibility: 'click' }"
                  @dayclick="formDataChanged(values)">
                  <template #default="{ inputValue, inputEvents }">
                    <div class="relative flex-grow">
                      <svg
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        class="absolute w-4 h-full mx-2 text-gray-600 pointer-events-none">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>

                      <input
                        :value="inputValue"
                        placeholder="End date"
                        class="flex-grow w-full px-10 py-2 pl-8 pr-2 transition ease-in-out bg-white border rounded-lg outline-none text-light-body-text dark:text-dark-body-text border-light-divider-border dark:border-dark-divider-border focus:ring-1 focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue focus:border-light-brand-blue dark:focus:border-dark-brand-blue duration-side-menu-transition-duration"
                        :class="[
                          !isBoxBackground
                            ? 'bg-light-screen-background dark:bg-dark-screen-background'
                            : 'bg-light-box-background dark:bg-dark-box-background'
                        ]"
                        v-on="inputEvents">
                    </div>
                  </template>
                </DatePicker>
              </div>

              <div class="flex space-x-6 my-2">
                <div class="flex space-x-2 items-center w-2/5">
                  <div class="mr-2">
                    {{ $t("Text Color") }}:
                    <span class="text-xs text-common-grey">{{ graph_text_color }}</span>
                  </div>
                  <ColorPickerList v-model:current-item="graph_text_color" @update:currentItem="formDataChanged(values)" />
                </div>
                <div class="flex space-x-2 items-center w-2/5">
                  <div class="mr-2">
                    {{ $t("Background Color") }}:
                    <span class="text-xs text-common-grey">{{ graph_background_color }}</span>
                  </div>
                  <ColorPickerList v-model:current-item="graph_background_color" @update:currentItem="formDataChanged(values)" />
                </div>
              </div>

              <div
                v-if="(values['type'] == 'horizontal_bars' || values['type'] == 'vertical_bars') && selected_series.length > 1"
                class="flex flex-col w-1/2 gap-2">
                <label class="block text-base font-medium text-gray-700">{{ $t("Stacked Bars") }}</label>

                <Switch
                  v-model="stacked_bars"
                  :class="[
                    stacked_bars ? 'bg-blue' : 'bg-gray-200',
                    'relative inline-flex flex-shrink-0 h-7 w-12 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
                  ]">
                  <span
                    aria-hidden="true"
                    :class="[
                      stacked_bars && $store.state.portfolio.ui_options?.language == 'ar'
                        ? '-translate-x-5'
                        : stacked_bars
                          ? 'translate-x-5'
                          : 'translate-x-0',
                      'pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                    ]" />
                </Switch>
              </div>
              <div
                :class="[filter_error ? 'border-red-400' : '']"
                class="w-full p-2 rounded-md flex flex-col border-[1px] border-gray-300 dark:border-dark-divider-border pb-10">
                <div>{{ $t("Graph filter") }}:</div>

                <AdvancedMetricsFilterForm
                  :object-type="graph_object_type"
                  :initial-filter="initialFilter"
                  @update="
                    ($event) => {
                      handleUpdateFilter($event)
                      formDataChanged(values)
                    }
                  " />

                <span v-if="filter_error" class="text-red-400">{{ $t("Filter is invalid") }}</span>
              </div>
            </div>

            <div v-if="!inNewComponentForm" class="flex flex-row justify-between w-full gap-2">
              <div class="w-1/2 m-0 cancel-btn" @click="is_loading ? null : $emit('cancel')">
                {{ $t("Cancel") }}
              </div>
              <button
                :class="[!selected_series.length ? 'opacity-50' : '']"
                :disable="selected_series.length == 0"
                type="submit"
                class="w-1/2 primary-btn">
                {{ is_loading ? $t("Loading...") : $t("Add Graph") }}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script>
import { Switch } from "@headlessui/vue"
import * as Yup from "yup"
import TextInput from "@/components/shared/TextInput.vue"
import SelectInput from "@/components/shared/SelectInput.vue"
import { Form } from "vee-validate"
import { DatePicker } from "v-calendar"
import { Trash2, RotateCw } from "lucide-vue-next"
import ComboBoxInput from "../shared/ComboBoxInput.vue"
import { utils } from "@/store/helpers"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import ColorPickerList from "@/components/shared/ColorPickerList.vue"
import AdvancedMetricsFilterForm from "@/components/forms/AdvancedMetricsFilterForm.vue"
import { transformMetricFilterToServerFilter } from "@/services/data.transformers"
import { useI18n } from "vue-i18n"

export default {
  components: {
    DatePicker,
    AdvancedMetricsFilterForm,
    ComboBoxInput,
    Switch,
    TextInput,
    SelectInput,
    Trash2,
    RotateCw,
    Form,
    ColorPickerList
  },
  props: {
    inNewComponentForm: {
      type: Boolean,
      default: false
    },
    objectType: {
      type: String,
      required: false
    },
    pageReference: {
      type: String,
      required: true
    },
    initialFilter: {
      type: String,
      default: "",
      required: false
    }
  },
  emits: ["cancel", "submitted", "done", "setComponentData"],
  setup() {
    const { t } = useI18n()
    const schema = Yup.object().shape({
      name: Yup.string().required(t("Graph name is required")),
      graph_type: Yup.string().required(),
      is_public: Yup.mixed().oneOf(["t", "f"]),
      series: Yup.array().of(
        Yup.object().shape({
          metric: Yup.string().required(t("Metric is required")),
          aggregation_method: Yup.string().required(t("Aggregation method is required")),
          space_color: Yup.string().required(t("Color is required"))
        })
      ),
      max_limit: Yup.number(t("Number is required")).nullable(),
      axis: Yup.string().nullable().required(t("Graph axis is required")),
      truncate_by: Yup.string().nullable().oneOf(["day", "month", "year", "quarter", "week"]),
      aggregate: Yup.mixed().oneOf(["t", "f"]),
      order_sort: Yup.string()
    })
    return {
      schema
    }
  },

  data() {
    return {
      graph_text_color: "black",
      graph_background_color: "white",
      isBoxBackground: false,
      public_private: [
        { name: this.$t("Public"), value: "t" },
        { name: this.$t("Private (Me)"), value: "f" }
      ],
      graph_object_type: null,
      graph_types: [
        { name: this.$t("Horizontal Bars"), value: "horizontal_bars" },
        { name: this.$t("Vertical Bars"), value: "vertical_bars" },
        { name: this.$t("Patterned Bars"), value: "patterned_bars" },
        { name: this.$t("Stacked Horizontal Bars"), value: "stacked_horizontal_bars" },
        { name: this.$t("Stacked Vertical Bars"), value: "stacked_vertical_bars" },
        { name: this.$t("100% Stacked Bars"), value: "100_stacked_bars" },
        { name: this.$t("Table"), value: "table" },
        { name: this.$t("Line"), value: "line" },
        { name: this.$t("Dashed Line"), value: "dashed_line" },
        { name: this.$t("Brush"), value: "brush" },
        { name: this.$t("Pie"), value: "pie" },
        { name: this.$t("Donut"), value: "donut_chart" },
        { name: this.$t("PolarArea"), value: "polarArea" },
        { name: this.$t("Polar Monochrome"), value: "polarMonochrome" },
        { name: this.$t("Heat Map"), value: "heatmap" },
        { name: this.$t("Scatter"), value: "scatter" },
        { name: this.$t("Radial Bar"), value: "radialBar" },
        { name: this.$t("Area"), value: "area" },
        { name: this.$t("Spline Area"), value: "spline_area" },
        { name: this.$t("Negative Area"), value: "negative_area" },
        // { name: this.$t()"Range Area", value: "rangeArea" },
        // { name: this.$t()"Range Bar", value: "rangeBar" },
        { name: this.$t("Radar"), value: "radar" },
        // { name: this.$t()"BoxPlot", value: "boxPlot" },
        // { name: this.$t()"Candlestick", value: "candlestick" },
        { name: this.$t("Treemap"), value: "treemap" },
        { name: this.$t("Mixed Charts"), value: "mixed_charts" },
        { name: this.$t("Pyramid Chart"), value: "pyramid_chart" },
        { name: this.$t("Funnel Chart"), value: "funnel_chart" },
        { name: this.$t("Stepline Chart"), value: "stepline_chart" },
        { name: this.$t("Gradient Chart"), value: "gradient_chart" }
      ],
      chart_types: [
        { name: this.$t("Column"), value: "column" },
        { name: this.$t("Line"), value: "line" },
        { name: this.$t("Area"), value: "area" }
      ],

      max_limits: [
        { name: "5", value: 5 },
        { name: "10", value: 10 },
        { name: "15", value: 15 },
        { name: "20", value: 20 }
      ],

      order_sorts: [
        { name: this.$t("Default"), value: "random" },
        { name: this.$t("Descending"), value: "desc" },
        { name: this.$t("Ascending"), value: "asce" }
      ],
      truncate_by_types: [
        { name: this.$t("Day"), value: "day" },
        { name: this.$t("Week"), value: "week" },
        { name: this.$t("Month"), value: "month" },
        { name: this.$t("Year"), value: "year" },
        { name: this.$t("Quarter"), value: "quarter" }
      ],

      metrics_aggregation_methods: [
        { name: this.$t("SUM"), value: "sum" },
        { name: this.$t("MIN"), value: "min" },
        { name: this.$t("MAX"), value: "max" },
        { name: this.$t("COUNT"), value: "count" },
        { name: this.$t("AVERAGE"), value: "average" },
        { name: this.$t("WEIGHTED"), value: "weighted" }
      ],
      extra_axis_metrics: [
        {
          reference_name: "history_date",
          id: "history_date",
          name: this.$t("Change date"),
          data_type: "DateTime",
          object_type: this.graph_object_type
        },
        {
          reference_name: "datetime_created",
          id: "datetime_created",
          name: this.$t("Create date"),
          data_type: "DateTime",
          object_type: this.graph_object_type
        },
        {
          reference_name: "parent_id",
          id: "parent_id",
          name: this.$t("Parent"),
          data_type: "String",
          object_type: this.graph_object_type
        },
        {
          reference_name: "owner__id",
          id: "owner__id",
          name: this.$t("Owner"),
          data_type: "String",
          object_type: "record"
        },
        {
          reference_name: "co_owners__id",
          id: "co_owners__id",
          name: this.$t("Co-Owners"),
          data_type: "List",
          object_type: "record"
        }
      ],

      filter: "",
      initial_values: {},
      filter_error: false,
      is_filter_valid: false,
      aggregate_series: false,
      stacked_bars: false,
      selected_series: [],
      is_loading: false,
      axis_metrics: [],
      series_metrics: [],
      from_date: null,
      to_date: null
    }
  },

  watch: {
    graph_object_type() {
      this.axis_metrics = this.$filter(this.$store.state.metrics, {
        object_type: this.graph_object_type
      }).concat(this.extra_axis_metrics)
      this.series_metrics = this.axis_metrics.filter((m) => m.data_type == "Number")
      if (this.series_metrics) {
        this.selected_series = []
        this.selected_series.push({
          id: 0,
          metric: this.series_metrics[0].id,
          aggregation_method: "average",
          chart_type: this.chart_types[0].value,
          space_color: this.$store.state.data.graphs_default_colors[0]
        })
        this.filter = this.$utils.clone(this.initialFilter)
      }
    }
  },
  created() {
    if (this.objectType && this.objectType != "default") {
      this.graph_object_type = this.objectType
    }
    if (this.graph_object_type) {
      this.axis_metrics = this.$filter(this.$store.state.metrics, {
        object_type: this.graph_object_type
      }).concat(this.extra_axis_metrics)
    } else {
      this.axis_metrics = utils.clone(this.$store.state.metrics).concat(this.extra_axis_metrics)
    }
    this.series_metrics = this.axis_metrics.filter((m) => m.data_type == "Number")
    if (this.series_metrics) {
      this.selected_series.push({
        id: 0,
        metric: this.series_metrics[0].id,
        aggregation_method: "average",
        chart_type: this.chart_types[0].value,
        space_color: this.$store.state.data.graphs_default_colors[0]
      })
      this.filter = this.$utils.clone(this.initialFilter)
    }
    this.initial_values = {
      graph_type: this.$t("horizontal_bars"),
      is_public: "t",
      max_limit: 5,
      axis: this.axis_metrics.find((m) => m.name.search("name") != -1)?.id,
      series: [],
      order_sort: "random"
    }
  },

  methods: {
    handleUpdateFilter(filter) {
      const filter_cleaned = []
      filter.forEach((item) => {
        if (item.data_type == "String") {
          if (Array.isArray(item.value)) {
            item.value = String(item.value)
          }
          // let value = item.value.replace(/[']+/g, "")
          filter_cleaned.push({
            ...item,
            value: item.value == null ? "" : item.value.trim()
          })
        } else {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          filter_cleaned.push({ ...item, value: item.value })
        }
      })
      let parsed_filter = transformMetricFilterToServerFilter([...filter_cleaned])

      this.filter = parsed_filter.filter
      this.is_filter_valid = parsed_filter.valid
    },
    isDateBasedAxis(values) {
      return (
        values["axis"] &&
        this.axis_metrics.filter((m) => values["axis"] == m.id && (m.data_type == "Date" || m.data_type == "DateTime")).length > 0
      )
    },
    formDataChanged(values) {
      this.$emit("setComponentData", {
        ...values,
        axis: values["axis"] == "history_date" ? null : values["axis"],
        required_filled: values.name && values.axis && (this.filter ? this.is_filter_valid : true),
        type: this.graph_object_type,
        selected_series: this.selected_series,
        filter: this.filter,
        is_filter_valid: this.is_filter_valid,
        from_date: this.from_date ? this.$moment(this.from_date).format("YYYY-MM-DD") : null,
        to_date: this.to_date ? this.$moment(this.to_date).format("YYYY-MM-DD") : null,
        graph_text_color: this.graph_text_color,
        graph_background_color: this.graph_background_color
      })
    },
    handleSelectAggregation(e, i) {
      this.selected_series[i]["aggregation_method"] = e
      this.series_metrics = utils.clone(this.axis_metrics)
    },
    createGraph(values) {
      this.is_loading = true

      if (this.filter && !this.is_filter_valid) {
        this.filter_error = true
      } else {
        let data = {
          ...values,
          page_reference: this.pageReference,
          series: this.selected_series,
          type: this.graph_object_type
        }

        if (this.filter) {
          data = { ...data, filter: this.filter }
        }
        if (["history_date", "datetime_created", "parent_id", "owner__id", "co_owners__id"].includes(values["axis"])) {
          data["axis_field"] = data["axis"]
          data["axis"] = null
        }
        this.$store
          .dispatch("data/createGraph", data)
          .then((res) => {
            this.$emit("done", res)
            this.$emitter.emit(ALL_EMITTERS.GRAPH_CREATED, res)
            this.is_loading = false
          })
          .catch(() => {
            this.is_loading = false
          })
      }
    }
  }
}
</script>
